import { clearToken, getToken, isMiniApp } from "./auth";
import type {
  AuthConfig,
  AuthResult,
  DataSettings,
  JobInfo,
  JobOptions,
  MeInfo,
  OperationInfo,
  RequestBuild,
  RequestBuildResult,
  RequestConfig,
  UserInfo,
  UserRole,
} from "./types";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

function initData(): string {
  return typeof window !== "undefined"
    ? window.Telegram?.WebApp?.initData ?? ""
    : "";
}

// Колбэк, вызываемый при 401 в веб-режиме (после очистки токена) —
// AuthProvider подписывается, чтобы показать экран входа.
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

function authHeaders(headers: Headers): void {
  if (isMiniApp()) {
    headers.set("X-Telegram-Init-Data", initData());
    return;
  }
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  authHeaders(headers);
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    // В вебе протухшая/битая сессия -> чистим токен и зовём логин.
    if (res.status === 401 && !isMiniApp()) {
      clearToken();
      unauthorizedHandler?.();
    }
    let detail: string;
    try {
      const data = await res.json();
      detail = data.detail ?? JSON.stringify(data);
    } catch {
      detail = await res.text();
    }
    throw new Error(`${res.status}: ${detail}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  getAuthConfig(): Promise<AuthConfig> {
    return request<AuthConfig>("/auth/config");
  },
  loginWithTelegram(idToken: string): Promise<AuthResult> {
    return request<AuthResult>("/auth/telegram", {
      method: "POST",
      body: JSON.stringify({ id_token: idToken }),
    });
  },
  listOperations(): Promise<OperationInfo[]> {
    return request<OperationInfo[]>("/operations");
  },
  createJob(
    operation_id: string,
    table_url: string,
    options: JobOptions,
  ): Promise<{ job_id: string }> {
    return request<{ job_id: string }>("/jobs", {
      method: "POST",
      body: JSON.stringify({ operation_id, table_url, options }),
    });
  },
  getJob(job_id: string): Promise<JobInfo> {
    return request<JobInfo>(`/jobs/${job_id}`);
  },
  getMe(): Promise<MeInfo> {
    return request<MeInfo>("/me");
  },
  listUsers(): Promise<UserInfo[]> {
    return request<UserInfo[]>("/users");
  },
  getUser(tgid: number): Promise<UserInfo> {
    return request<UserInfo>(`/users/${tgid}`);
  },
  updateUser(
    tgid: number,
    patch: { role?: UserRole; permissions?: string[] },
  ): Promise<UserInfo> {
    return request<UserInfo>(`/users/${tgid}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  getDataSettings(): Promise<DataSettings> {
    return request<DataSettings>("/data");
  },
  updateDataSettings(body: DataSettings): Promise<DataSettings> {
    return request<DataSettings>("/data", {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  getRequestConfig(): Promise<RequestConfig> {
    return request<RequestConfig>("/request/config");
  },
  buildRequest(body: RequestBuild): Promise<RequestBuildResult> {
    return request<RequestBuildResult>("/request/build", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};
